import { Fragment, useState, useMemo, useEffect } from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
const PAGINATION_THRESHOLD = 50;
export interface SortableColumn<T> {
  key: string;
  label: React.ReactNode;
  width?: string;
  render: (row: T) => React.ReactNode;
  sortValue?: (row: T) => string | number;
  align?: 'left' | 'right' | 'center';
}
interface SortState {
  key: string | null;
  dir: 'asc' | 'desc';
}
interface SortableTableProps<T> {
  columns: SortableColumn<T>[];
  data: T[];
  defaultSort?: {
    key: string;
    dir: 'asc' | 'desc';
  };
  onRowClick?: (row: T) => void;
  rowKey: (row: T) => string;
  className?: string;
  renderExpandedRow?: (row: T) => React.ReactNode;
  footer?: React.ReactNode;
  onSortedDataChange?: (data: T[]) => void;
  pageSize?: number;
}
function getComparableValue<T>(row: T, col: SortableColumn<T>): string | number {
  if (col.sortValue) return col.sortValue(row);
  const val = (row as Record<string, unknown>)[col.key];
  if (typeof val === 'string') return val;
  if (typeof val === 'number') return val;
  return String(val ?? '');
}
function compareValues(a: string | number, b: string | number, dir: 'asc' | 'desc'): number {
  let result: number;
  if (typeof a === 'string' && typeof b === 'string') {
    result = a.localeCompare(b);
  } else {
    result = (a as number) - (b as number);
  }
  return dir === 'asc' ? result : -result;
}
export function SortableTable<T>({
  columns,
  data,
  defaultSort,
  onRowClick,
  rowKey,
  className = 'tbl',
  renderExpandedRow,
  footer,
  onSortedDataChange,
  pageSize
}: SortableTableProps<T>) {
  const [sort, setSort] = useState<SortState>({
    key: defaultSort?.key ?? null,
    dir: defaultSort?.dir ?? 'desc'
  });
  const [page, setPage] = useState(0);
  const sortedData = useMemo(() => {
    if (!sort.key) return data;
    const col = columns.find(c => c.key === sort.key);
    if (!col) return data;
    return [...data].sort((a, b) => compareValues(getComparableValue(a, col), getComparableValue(b, col), sort.dir));
  }, [data, sort.key, sort.dir, columns]);
  useEffect(() => {
    onSortedDataChange?.(sortedData);
  }, [sortedData, onSortedDataChange]);
  useEffect(() => {
    setPage(0);
  }, [data, sort.key, sort.dir]);
  const paginated = Boolean(pageSize) && sortedData.length > PAGINATION_THRESHOLD;
  const pageCount = pageSize ? Math.max(1, Math.ceil(sortedData.length / pageSize)) : 1;
  const currentPage = Math.min(page, pageCount - 1);
  const pageData = paginated && pageSize ? sortedData.slice(currentPage * pageSize, currentPage * pageSize + pageSize) : sortedData;
  const handleClick = (colKey: string) => {
    setSort(prev => {
      if (prev.key === colKey) {
        if (prev.dir === 'asc') return {
          key: colKey,
          dir: 'desc'
        };
        return {
          key: null,
          dir: 'desc'
        };
      }
      return {
        key: colKey,
        dir: 'asc'
      };
    });
  };
  const sortIndicator = (colKey: string) => {
    const isActive = sort.key === colKey;
    return <span style={{
      marginLeft: 3,
      fontSize: 8,
      color: isActive ? '#fa0f00' : '#c0c5ce'
    }} data-id="element-1196" data-mp="1l77kb46zzikr">
        {isActive ? sort.dir === 'asc' ? '▲' : '▼' : '▲'}
      </span>;
  };
  const renderPagination = (position: 'top' | 'bottom') => {
    if (!paginated || !pageSize) return null;
    const from = currentPage * pageSize + 1;
    const to = Math.min(currentPage * pageSize + pageSize, sortedData.length);
    return <div className={`pgn-bar pgn-${position}`} data-id="element-1197" data-mp="12c0yas55n0i9">
        <span className="pgn-count" data-id="element-1198" data-mp="xc4uaswgj92p">
          <strong data-id="element-1199" data-mp="xd7mr59fhjkq">{from}–{to}</strong> of <strong data-id="element-1200" data-mp="1bamb06ny3lb4">{sortedData.length}</strong>
        </span>
        <div className="pgn-controls" data-id="element-1201" data-mp="1bc76byi338a1">
          <button onClick={() => setPage(0)} disabled={currentPage === 0} className="pgn-btn" aria-label="First page" data-id="element-1202" data-mp="178redfgylw3o">
            <ChevronsLeft size={13} data-id="element-1203" data-mp="2iog1q7y4kx50" />
          </button>
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={currentPage === 0} className="pgn-btn" aria-label="Previous page" data-id="element-1204" data-mp="2ino5cuwpevdo">
            <ChevronLeft size={13} data-id="element-1205" data-mp="3i0pz7cpmwhxz" />
          </button>
          <span className="pgn-page" data-id="element-1206" data-mp="3mzkjkj392ek7">{currentPage + 1} / {pageCount}</span>
          <button onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))} disabled={currentPage >= pageCount - 1} className="pgn-btn" aria-label="Next page" data-id="element-1207" data-mp="3n1nf1in68zwb">
            <ChevronRight size={13} data-id="element-1208" data-mp="4tfshyuqbvna" />
          </button>
          <button onClick={() => setPage(pageCount - 1)} disabled={currentPage >= pageCount - 1} className="pgn-btn" aria-label="Last page" data-id="element-1209" data-mp="4rv2mmjd10vr">
            <ChevronsRight size={13} data-id="element-1210" data-mp="eiplri1b7kcz" />
          </button>
        </div>
      </div>;
  };
  return <>
      {renderPagination('top')}
      <table className={className} data-id="element-1211" data-mp="oh5f7rnt75pn">
        <thead data-id="element-1212" data-mp="oi6ru0kh409h">
          <tr data-id="element-1213" data-mp="ohx3ehf2u15w">
            {columns.map(col => <th key={col.key} style={{
            width: col.width,
            cursor: 'pointer',
            userSelect: 'none',
            textAlign: col.align
          }} onClick={() => handleClick(col.key)} data-id="element-1214" data-mp="28zzhv5elrs7h">
                {col.label}{sortIndicator(col.key)}
              </th>)}
          </tr>
        </thead>
        <tbody data-id="element-1215" data-mp="2row36js0vadk">
          {pageData.map((row, i) => <Fragment key={rowKey(row) || i} data-id="element-1216" data-mp="2mre37ivzg5hp" data-mpi={i}>
              <tr onClick={onRowClick ? () => onRowClick(row) : undefined} data-id="element-1217" data-mp="2mrmcz1wt6uoo">
                {columns.map(col => <td key={col.key} style={{
              textAlign: col.align
            }} data-id="element-1218" data-mp="2hyb5cyk6a9vp">
                    {col.render(row)}
                  </td>)}
              </tr>
              {renderExpandedRow?.(row)}
            </Fragment>)}
        </tbody>
        {footer && <tfoot data-id="element-1219" data-mp="35p6rpg5v0cvk">{footer}</tfoot>}
      </table>
      {renderPagination('bottom')}
    </>;
}